import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useInventoryAppServices } from '@/app/services/InventoryAppServicesContext';
import { updateInventorizationContext } from '@/features/inventorization/domain/inventorization-context';
import { updateInventorizationRegisterReading } from '@/features/inventorization/domain/inventorization-reading';
import { DEFAULT_INVENTORIZATION_REGISTER_ID } from '@/features/inventorization/domain/inventorization-registers';
import { createOcrJobForPhotoSlot } from '@/features/inventorization/photos/photo-slot-ocr-job';
import type { CapturedPhotoAsset } from '@/shared/camera/photo-capture-adapter';
import { createPhotoRefFromAsset } from '@/shared/camera/photo-capture-adapter';
import { evaluateCapturedPhotoQuality } from '@/shared/camera/photo-quality';
import { type LocalPhotoRef, PHOTO_CONTEXTS } from '@/shared/capture/capture-session';
import type { PhotoSlotDefinition } from '@/shared/capture/inventorization-photo-slots';
import { deleteLocalPhotoBlobs } from '@/shared/db/photo-blob-cleanup';
import { messages } from './messages';

interface CapturePhotoSlotState {
  error: string | null;
  isCapturingSlotId: string | null;
}

export function useCapturePhotoSlot(sessionId: string) {
  const intl = useIntl();
  const services = useInventoryAppServices();
  const repositories = useMemo(
    () => ({
      captureSessions: services.captureSessions,
      photoBlobs: services.photoBlobs,
    }),
    [services.captureSessions, services.photoBlobs],
  );
  const [state, setState] = useState<CapturePhotoSlotState>({
    error: null,
    isCapturingSlotId: null,
  });

  async function captureSlot(slot: PhotoSlotDefinition, asset: CapturedPhotoAsset, replacedPhoto?: LocalPhotoRef) {
    setState({ error: null, isCapturingSlotId: slot.id });

    try {
      const quality = evaluateCapturedPhotoQuality(asset, asset.capturedAt);
      const photo = {
        ...createPhotoRefFromAsset(
          {
            context: slot.context,
            registerId: replacedPhoto ? replacedPhoto.registerId : slot.registerId,
            sessionId,
          },
          asset,
        ),
        quality,
      };
      await repositories.photoBlobs.save({
        id: photo.localBlobId,
        blob: asset.originalBlob,
        createdAt: photo.capturedAt,
        mimeType: asset.mimeType,
        sizeBytes: asset.originalBlob.size,
      });
      if (photo.thumbnailBlobId) {
        await repositories.photoBlobs.save({
          id: photo.thumbnailBlobId,
          blob: asset.thumbnailBlob,
          createdAt: photo.capturedAt,
          mimeType: asset.thumbnailBlob.type || asset.mimeType,
          sizeBytes: asset.thumbnailBlob.size,
        });
      }
      const ocrJob = createOcrJobForPhotoSlot({
        createdAt: photo.capturedAt,
        photoId: photo.id,
        sessionId,
        slot,
      });

      await repositories.captureSessions.update(sessionId, session => {
        const extractions = replacedPhoto
          ? session.aiExtractions.filter(extraction => extraction.photoId !== replacedPhoto.id)
          : session.aiExtractions;
        const withPhoto = {
          ...session,
          aiExtractions: ocrJob ? [...extractions, ocrJob] : extractions,
          photos: replacedPhoto
            ? session.photos.map(existing => (existing.id === replacedPhoto.id ? photo : existing))
            : [...session.photos, photo],
          updatedAt: photo.capturedAt,
        };

        if (slot.context === PHOTO_CONTEXTS.context) {
          return updateInventorizationContext(withPhoto, {}, photo.capturedAt);
        }

        if (slot.context === PHOTO_CONTEXTS.register) {
          const registerId = photo.registerId ?? DEFAULT_INVENTORIZATION_REGISTER_ID;

          return updateInventorizationRegisterReading(
            withPhoto,
            registerId,
            { photoRef: registerId },
            photo.capturedAt,
          );
        }

        return withPhoto;
      });
      if (replacedPhoto) {
        await deleteLocalPhotoBlobs(repositories.photoBlobs, [replacedPhoto]);
      }
      setState({ error: null, isCapturingSlotId: null });

      return photo;
    } catch (error) {
      setState({
        error: error instanceof Error ? error.message : intl.formatMessage(messages.photoWizardCaptureError),
        isCapturingSlotId: null,
      });

      return undefined;
    }
  }

  async function deletePhoto(photo: LocalPhotoRef) {
    setState({ error: null, isCapturingSlotId: photo.id });

    try {
      await repositories.captureSessions.update(sessionId, session => ({
        ...session,
        aiExtractions: session.aiExtractions.filter(extraction => extraction.photoId !== photo.id),
        photos: session.photos.filter(sessionPhoto => sessionPhoto.id !== photo.id),
        updatedAt: new Date().toISOString(),
      }));
      await deleteLocalPhotoBlobs(repositories.photoBlobs, [photo]);
      setState({ error: null, isCapturingSlotId: null });
    } catch (error) {
      setState({
        error: error instanceof Error ? error.message : intl.formatMessage(messages.photoWizardDeleteError),
        isCapturingSlotId: null,
      });
    }
  }

  return {
    ...state,
    captureSlot,
    deletePhoto,
  };
}
